import React, { useRef, useState, useEffect, useCallback } from 'react';

const SignaturePad = ({ onSave, onCancel, title = 'Signer' }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);
  const lastPos = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    canvas.width  = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#1a1a1a';
    ctx.lineWidth   = 2;
    ctx.lineCap     = 'round';
    ctx.lineJoin    = 'round';
  }, []);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const src  = e.touches ? e.touches[0] : e;
    return { x: src.clientX - rect.left, y: src.clientY - rect.top };
  };

  const startDraw = useCallback(e => {
    e.preventDefault();
    setIsDrawing(true);
    lastPos.current = getPos(e, canvasRef.current);
  }, []);

  const draw = useCallback(e => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx    = canvas.getContext('2d');
    const pos    = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPos.current = pos;
    setIsEmpty(false);
  }, [isDrawing]);

  const stopDraw = useCallback(() => setIsDrawing(false), []);

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx    = canvas.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setIsEmpty(true);
  };

  const save = () => {
    if (isEmpty) return;
    const dataUrl = canvasRef.current.toDataURL('image/png');
    onSave(dataUrl);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.6)',
      backdropFilter: 'blur(4px)',
      zIndex: 2000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '1rem',
    }}>
      <div style={{
        background: 'var(--bg-card)',
        borderRadius: '16px',
        padding: '1.5rem',
        width: '100%',
        maxWidth: '520px',
        boxShadow: '0 24px 64px rgba(0,0,0,0.3)',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <i className="fas fa-pen-nib" style={{ color: 'var(--accent-start)' }}></i> {title}
          </h3>
          <button onClick={onCancel} style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border)', borderRadius: '8px', width: 32, height: 32, cursor: 'pointer', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Indications */}
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.82rem', margin: '0 0 0.75rem', textAlign: 'center' }}>
          Dessinez votre signature dans le cadre ci-dessous
        </p>

        {/* Zone de dessin */}
        <div style={{ position: 'relative', border: '2px dashed var(--border)', borderRadius: '12px', overflow: 'hidden', background: '#fff', height: 180 }}>
          <canvas
            ref={canvasRef}
            style={{ display: 'block', width: '100%', height: '100%', cursor: 'crosshair', touchAction: 'none' }}
            onMouseDown={startDraw}
            onMouseMove={draw}
            onMouseUp={stopDraw}
            onMouseLeave={stopDraw}
            onTouchStart={startDraw}
            onTouchMove={draw}
            onTouchEnd={stopDraw}
          />
          {isEmpty && (
            <div style={{
              position: 'absolute', inset: 0,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              pointerEvents: 'none', gap: '0.5rem',
            }}>
              <i className="fas fa-signature" style={{ fontSize: '2rem', color: '#d1d5db' }}></i>
              <span style={{ color: '#d1d5db', fontSize: '0.85rem' }}>Signez ici</span>
            </div>
          )}
          {/* Ligne de base */}
          <div style={{ position: 'absolute', bottom: '28px', left: '10%', right: '10%', height: '1px', background: '#e5e7eb', pointerEvents: 'none' }}></div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button
            onClick={clear}
            style={{ padding: '0.65rem 1.25rem', border: '1px solid var(--border)', borderRadius: '10px', background: 'var(--bg-subtle)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.88rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}
          >
            <i className="fas fa-eraser"></i> Effacer
          </button>
          <button onClick={onCancel} style={{ padding: '0.65rem 1.25rem', border: '1px solid var(--border)', borderRadius: '10px', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.88rem' }}>
            Annuler
          </button>
          <button
            onClick={save}
            disabled={isEmpty}
            style={{
              padding: '0.65rem 1.5rem', border: 'none', borderRadius: '10px',
              background: isEmpty ? '#d1d5db' : 'linear-gradient(135deg, var(--accent-start), var(--accent-end))',
              color: '#fff', cursor: isEmpty ? 'not-allowed' : 'pointer',
              fontSize: '0.88rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.4rem',
              transition: 'all 0.2s',
            }}
          >
            <i className="fas fa-check"></i> Appliquer la signature
          </button>
        </div>
      </div>
    </div>
  );
};

export default SignaturePad;